/**
 * @fileoverview 乗下船表（船員向け・閲覧専用）
 * @description 自分が配乗されている船の乗下船表をスマホで見る画面。
 *   - 陸上が公開した月までしか表示しない（月単位）。未公開なら表は出さない
 *   - 累計日数（乗船数・休み数）は表示しない（サーバーからも返ってこない）
 *   - 2隻以上に配乗されている人は上部のボタンで船を切り替えられる
 *   - 機能フラグ crew_boarding_grid の会社は、紙のExcel乗下船表と同じ見た目の
 *     「一覧表」（名前×日付に乗・休・動静帯）を第1ビューとして出し、従来の表示は
 *     「カレンダー」ビューとして切替ボタンで残す（シーステージ様の紙様式に合わせた）。
 *     一覧表でも乗組員ごとの乗船日数・休み数（紙の右側の集計列）は出さない。
 *   - スマホで読みやすいよう、自分の分は7列の月カレンダー、他の乗組員は1人1行のリストで見せる
 *   - いちばん上に「あなたの乗船予定」を置き、船をまたいだ乗り継ぎと陸上（休み）を時系列で出す。
 *     1隻1ヶ月の表だけでは「ずっと乗船しているように見える」「他の船に移った日が分からない」ため。
 *   - カレンダーの日を押すと、その日にこの船へ誰が乗っているか（乗船・下船・休み・他の船）を出す。
 *   - 沖で圏外になるため、一度開いた内容は端末に保存し、次からは圏外でもそのまま見られる
 *     （勤怠履歴と同じやり方。表示中は「オフライン」と出して、いつ時点の内容かを添える）。
 *   API: GET /boarding-sheet/crew/vessels ／ GET /boarding-sheet/crew ／ GET /boarding-sheet/crew/my-schedule
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BottomNav } from "../components/BottomNav";
import { AppHeader } from "../components/AppHeader";
import { apiFetch } from "../services/api";
import { hasFeature } from "../utils/featureFlags";
import type { AuthUser } from "../hooks/useAuth";

interface Props { user: AuthUser }

/* ══════════ 型（サーバーの /boarding-sheet/crew に対応。集計値は無い） ══════════ */
interface DayCell {
  /** on=乗船 / off=陸上休み / none=配乗前 */
  s: "on" | "off" | "none";
  /** 乗船日 */ b?: 1;
  /** 下船日 */ d?: 1;
  /** 希望休（本人の分のみ） */ w?: string;
  /** 他の船に乗船 */ x?: 1;
  /** 他の船の船名 */ vn?: string;
  /** 他の船のID（タップでその船の表へ切り替える） */ vi?: string;
  /** 執職（辞令と異なる職で従事。紙の「執職船長」注記に相当） */ p?: string;
}
interface CrewRow {
  user_id: string; user_name: string; positions: string[];
  /** 辞令上の役職（未整備の会社は null → positions で代用） */
  rank_position?: string | null;
  is_me: boolean; days: DayCell[];
}
interface SheetEventItem { user_name: string; action: "board" | "disembark"; position: string | null }
interface SheetEvent {
  id: string; event_date: string; end_date: string | null; event_type: string;
  port_name: string | null; berth_note: string | null; note: string | null;
  items: SheetEventItem[];
}
interface CrewSheet {
  vessel_id: string; vessel_name: string; month: string; n_days: number;
  published: boolean; published_until: string | null; visible_days: number;
  crew: CrewRow[]; events: SheetEvent[];
}
interface Vessel { id: string; vessel_name: string }

/** 船をまたいだ自分の予定（GET /crew/my-schedule）。kind=shore は陸上（休み）の期間 */
interface ScheduleSeg {
  kind: "board" | "shore";
  start: string; end: string | null; open?: 1;
  vessel_id?: string; vessel_name?: string; position?: string | null; status?: string;
}

const EVENT_TYPE: Record<string, { label: string; color: string; bg: string }> = {
  change: { label: "交代", color: "#0057d9", bg: "#eff6ff" },
  berth: { label: "仮バース・停泊", color: "#7c3aed", bg: "#f5f3ff" },
  dock: { label: "ドック・入渠", color: "#b45309", bg: "#fdf3e3" },
  other: { label: "その他", color: "#64748b", bg: "#f1f5f9" },
};

const YOUBI = ["日", "月", "火", "水", "木", "金", "土"];

/* ══════════ オフライン保存（沖では圏外になるので、開いた内容を端末に残す） ══════════
   通信できないときは保存しておいた内容を出す。個人スマホでも船の端末でも同じ動きにする。 */
const CACHE_PREFIX = "catms_bsheet_";
/** 溜めすぎないように残す件数（1件あたり数KB） */
const CACHE_KEEP = 12;

function cacheSave(key: string, data: unknown): void {
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ data, savedAt: new Date().toISOString() }));
    const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX + "sheet_"));
    if (keys.length > CACHE_KEEP) {
      // 古い順に消す（保存日時が読めないものから捨てる）
      const withTime = keys.map(k => {
        let t = "";
        try { t = (JSON.parse(localStorage.getItem(k) ?? "{}") as { savedAt?: string }).savedAt ?? ""; } catch { /* 壊れた値 */ }
        return { k, t };
      }).sort((a, b) => a.t.localeCompare(b.t));
      withTime.slice(0, keys.length - CACHE_KEEP).forEach(x => localStorage.removeItem(x.k));
    }
  } catch { /* 容量超過などは無視（表示は続けられる） */ }
}

function cacheRead<T>(key: string): { data: T; savedAt: string } | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const v = JSON.parse(raw) as { data: T; savedAt: string };
    return v && v.data !== undefined ? v : null;
  } catch { return null; }
}

/** 2026-08-24T10:00:00.000Z → 8/24 19:00（日本時間） */
function savedAtLabel(iso: string): string {
  const d = new Date(new Date(iso).getTime() + 9 * 3600000);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()} ${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}

/** JSTの今日（YYYY-MM-DD） */
function todayYmd(): string {
  return new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);
}
function ymNow(): string { return todayYmd().slice(0, 7); }
function ymAdd(ym: string, n: number): string {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y!, m! - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
/** YYYY-MM-DD → 8/20 */
function md(ymd: string): string {
  return `${Number(ymd.slice(5, 7))}/${Number(ymd.slice(8, 10))}`;
}

/* ══════════ 一覧表ビュー（紙のExcel乗下船表と同じ見た目。crew_boarding_grid の会社の第1ビュー） ══════════
   名前を左に固定し、日付を横に並べて 乗・休・▲▼ を1マスずつ出す。動静（交代・ドック等）は
   日付の上に帯で重ねる。紙の右側にある乗船日数・休み数の集計列は意図的に出さない。 */

/** 1マスの見た目（カレンダービューと同じ配色に揃える） */
function gridCell(c?: DayCell): { t: string; bg: string; fg: string } {
  if (!c) return { t: "", bg: "#fff", fg: "#cbd5e1" };
  if (c.s === "on") {
    if (c.x) return { t: "他", bg: "#ede9fe", fg: "#6d28d9" };
    if (c.b) return { t: "▲", bg: "#0057d9", fg: "#fff" };
    if (c.d) return { t: "▼", bg: "#0057d9", fg: "#fff" };
    return { t: "乗", bg: "#dbeafe", fg: "#0b4e8f" };
  }
  if (c.w === "approved") return { t: "休", bg: "#bbf7d0", fg: "#166534" };
  if (c.w === "pending") return { t: "休", bg: "#fef3c7", fg: "#92400e" };
  if (c.s === "off") return { t: "休", bg: "#f8fafc", fg: "#94a3b8" };
  return { t: "", bg: "#fff", fg: "#cbd5e1" };
}

const CELL_W = 26;
const NAME_W = 100;

const SheetGrid: React.FC<{
  sheet: CrewSheet;
  days: Array<{ n: number; ymd: string; wd: number; isToday: boolean }>;
  vessels: Vessel[];
  jumpTo: (vid?: string, startYmd?: string) => void;
}> = ({ sheet, days, vessels, jumpTo }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // 今日の列が隠れない位置まで初期スクロール（月の後半に開いたときに空振りしないように）
  useEffect(() => {
    const i = days.findIndex(d => d.isToday);
    if (scrollRef.current) scrollRef.current.scrollLeft = i > 6 ? (i - 2) * CELL_W : 0;
  }, [sheet.vessel_id, sheet.month, days]);

  /** 動静帯: 日ごとにどのイベントが掛かるかを求め、連続した日をひとつの帯にまとめる */
  const bandRuns = useMemo(() => {
    const ym = sheet.month;
    const monthStart = `${ym}-01`;
    const byDay: (SheetEvent | null)[] = Array.from({ length: days.length }, () => null);
    for (const e of sheet.events) {
      const endYmd = e.end_date ?? e.event_date;
      if (endYmd < monthStart) continue;
      const s = e.event_date <= monthStart ? 1 : Number(e.event_date.slice(8, 10));
      if (s > days.length) continue;
      const en = endYmd.slice(0, 7) === ym ? Math.min(Number(endYmd.slice(8, 10)), days.length) : days.length;
      for (let i = s - 1; i < en; i++) if (!byDay[i]) byDay[i] = e; // 重なったら先のイベントを優先
    }
    const runs: Array<{ e: SheetEvent | null; len: number }> = [];
    for (const x of byDay) {
      const last = runs[runs.length - 1];
      if (last && last.e === x) last.len++;
      else runs.push({ e: x, len: 1 });
    }
    return runs;
  }, [sheet, days.length]);
  const hasBand = bandRuns.some(r => r.e);

  /** 日別の乗船・休人数（紙の下段と同じ。個人別の日数集計は出さない） */
  const dailyOn = useMemo(
    () => days.map((_, i) => sheet.crew.filter(u => u.days[i]?.s === "on" && !u.days[i]?.x).length),
    [sheet, days.length]);
  const dailyOff = useMemo(
    () => days.map((_, i) => sheet.crew.filter(u => u.days[i]?.s === "off").length),
    [sheet, days.length]);

  const stickyTh: React.CSSProperties = {
    position: "sticky", left: 0, zIndex: 3, minWidth: NAME_W, maxWidth: NAME_W,
    padding: "4px 8px", textAlign: "left", background: "#f8fafc",
    borderRight: "2px solid #e3e9ef", borderBottom: "2px solid #e3e9ef",
    fontSize: 10.5, fontWeight: 800, color: "#64748b", whiteSpace: "nowrap",
  };
  const stickyTd: React.CSSProperties = {
    position: "sticky", left: 0, zIndex: 2, minWidth: NAME_W, maxWidth: NAME_W,
    padding: "4px 8px", background: "#fff",
    borderRight: "2px solid #e3e9ef", borderBottom: "1px solid #edf1f5",
  };
  const dayTd: React.CSSProperties = {
    minWidth: CELL_W, maxWidth: CELL_W, height: 34, textAlign: "center",
    fontSize: 11.5, fontWeight: 800, borderBottom: "1px solid #edf1f5", borderLeft: "1px solid #f1f5f9",
  };

  const cellTip = (u: CrewRow, c: DayCell | undefined, ymd: string): string => {
    if (!c) return `${u.user_name} ${md(ymd)}`;
    const p = [`${u.user_name} ${md(ymd)}`];
    if (c.s === "on") p.push(c.x ? `${c.vn ?? "他の船"}に乗船` : c.b ? "乗船日" : c.d ? "下船日" : "乗船中");
    else if (c.s === "off") p.push("休（陸上）");
    if (c.p) p.push(`執職: ${c.p}として従事`);
    if (c.w === "approved") p.push("希望休（承認済）");
    else if (c.w === "pending") p.push("希望休（申請中）");
    return p.join(" / ");
  };

  return (
    <div style={{
      background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)", overflow: "hidden", marginBottom: 10,
    }}>
      <div style={{ padding: "12px 14px 8px", display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontSize: 15, fontWeight: 900, color: "#0f172a" }}>乗下船表（一覧）</div>
        <div style={{ fontSize: 11, color: "#94a3b8" }}>横にずらすと月末まで見られます</div>
      </div>
      <div ref={scrollRef} style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "max-content", minWidth: "100%" }}>
          <thead>
            {/* 動静帯（交代・仮バース・ドック等を日付の上に帯で出す。紙の「動静」行に相当） */}
            {hasBand && (
              <tr>
                <th style={{ ...stickyTh, borderBottom: "1px solid #edf1f5", fontSize: 9.5, color: "#94a3b8" }}>動静</th>
                {bandRuns.map((r, i) => {
                  if (!r.e) return <th key={i} colSpan={r.len} style={{ background: "#fff", borderBottom: "1px solid #edf1f5" }} />;
                  const t = EVENT_TYPE[r.e.event_type] ?? EVENT_TYPE["other"]!;
                  const label = r.e.event_type === "other" && r.e.note
                    ? r.e.note
                    : `${t.label}${r.e.port_name ? `・${r.e.port_name}` : ""}`;
                  const tip = `${md(r.e.event_date)}${r.e.end_date ? `〜${md(r.e.end_date)}` : ""} ${t.label}`
                    + (r.e.port_name ? ` ${r.e.port_name}` : "") + (r.e.note ? ` ※${r.e.note}` : "");
                  return (
                    <th key={i} colSpan={r.len} title={tip}
                      style={{
                        maxWidth: r.len * CELL_W, padding: "2px 3px", background: t.bg, color: t.color,
                        fontSize: 9, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        borderBottom: "1px solid #edf1f5", borderLeft: "1px solid #f1f5f9",
                      }}>
                      {label}
                    </th>
                  );
                })}
              </tr>
            )}
            {/* 日付 */}
            <tr>
              <th style={stickyTh}>名前／役職</th>
              {days.map(d => (
                <th key={d.n} style={{
                  minWidth: CELL_W, maxWidth: CELL_W, padding: "3px 0", textAlign: "center",
                  background: d.isToday ? "#fff3c9" : "#f8fafc",
                  borderBottom: "2px solid #e3e9ef", borderLeft: "1px solid #f1f5f9",
                }}>
                  <div style={{ fontSize: 10.5, fontWeight: 800, color: d.wd === 0 ? "#dc2626" : d.wd === 6 ? "#2563eb" : "#475569" }}>{d.n}</div>
                  <div style={{ fontSize: 8.5, color: d.wd === 0 ? "#dc2626" : d.wd === 6 ? "#2563eb" : "#94a3b8" }}>{YOUBI[d.wd]}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sheet.crew.map(u => {
              // 月内に執った職（執職）。紙の「執職船長」注記に相当する小さなチップで出す
              const duties = [...new Set(u.days.map(c => c?.p).filter((x): x is string => !!x))];
              return (
              <tr key={u.user_id}>
                <td style={{ ...stickyTd, background: u.is_me ? "#eff6ff" : "#fff" }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {u.user_name}
                    {u.is_me && <span style={{ marginLeft: 4, fontSize: 9, fontWeight: 800, color: "#0057d9", background: "#dbeafe", borderRadius: 4, padding: "1px 4px", verticalAlign: 1 }}>あなた</span>}
                  </div>
                  <div style={{ fontSize: 9.5, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {(u.rank_position || (u.positions.length ? u.positions.join("／") : "")) || "—"}
                    {duties.map(dp => (
                      <span key={dp} title={`この月に「${dp}」として職務に従事（執職）`}
                        style={{ marginLeft: 3, fontSize: 8.5, fontWeight: 800, color: "#92600a", background: "#fdf3e3", border: "1px solid #f2dca6", borderRadius: 4, padding: "0 3px" }}>
                        執{dp}
                      </span>
                    ))}
                  </div>
                </td>
                {days.map((d, i) => {
                  const c = u.days[i];
                  const st = gridCell(c);
                  const canJump = !!c?.x && !!c.vi && vessels.some(v => v.id === c.vi);
                  return (
                    <td key={d.n} title={cellTip(u, c, d.ymd)}
                      onClick={canJump ? () => jumpTo(c!.vi, d.ymd) : undefined}
                      style={{
                        ...dayTd, background: u.is_me && st.bg === "#fff" ? "#f5faff" : st.bg, color: st.fg,
                        cursor: canJump ? "pointer" : undefined,
                        // 執職日は金色の下線（陸上の乗下船表と同じ表現）
                        ...(c?.p && c.s === "on" ? { boxShadow: "inset 0 -3px 0 #f59e0b" } : {}),
                      }}>
                      {st.t}
                    </td>
                  );
                })}
              </tr>
              );
            })}
            {sheet.crew.length === 0 && (
              <tr>
                <td colSpan={1 + days.length} style={{ padding: 16, fontSize: 12.5, color: "#94a3b8", textAlign: "center" }}>
                  この月にこの船へ配乗された乗組員はいません
                </td>
              </tr>
            )}
          </tbody>
          {/* 日別の人数（紙の下段「乗・休」と同じ） */}
          {sheet.crew.length > 0 && (
            <tfoot>
              <tr>
                <td style={{ ...stickyTd, background: "#f8fafc", borderTop: "2px solid #e3e9ef", fontSize: 10.5, fontWeight: 800, color: "#0057d9" }}>乗船人数</td>
                {dailyOn.map((v, i) => (
                  <td key={i} style={{ ...dayTd, height: 24, fontSize: 10.5, color: "#0057d9", background: "#f8fafc", borderTop: "2px solid #e3e9ef" }}>{v}</td>
                ))}
              </tr>
              <tr>
                <td style={{ ...stickyTd, background: "#f8fafc", fontSize: 10.5, fontWeight: 800, color: "#94a3b8" }}>休人数</td>
                {dailyOff.map((v, i) => (
                  <td key={i} style={{ ...dayTd, height: 24, fontSize: 10.5, fontWeight: 700, color: "#94a3b8", background: "#f8fafc" }}>{v}</td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {/* 凡例 */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: 11.5, fontWeight: 700, color: "#475569", padding: "10px 14px 12px", borderTop: "1px solid #f1f5f9" }}>
        <span><i style={{ display: "inline-block", width: 13, height: 11, background: "#0057d9", borderRadius: 2, marginRight: 4, color: "#fff", fontSize: 8, lineHeight: "11px", textAlign: "center", fontStyle: "normal", fontWeight: 700 }}>▲</i>乗船日</span>
        <span><i style={{ display: "inline-block", width: 13, height: 11, background: "#0057d9", borderRadius: 2, marginRight: 4, color: "#fff", fontSize: 8, lineHeight: "11px", textAlign: "center", fontStyle: "normal", fontWeight: 700 }}>▼</i>下船日</span>
        <span><i style={{ display: "inline-block", width: 13, height: 11, background: "#dbeafe", borderRadius: 2, marginRight: 4 }} />乗＝乗船中</span>
        <span><i style={{ display: "inline-block", width: 13, height: 11, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 2, marginRight: 4 }} />休＝陸上休み</span>
        <span><i style={{ display: "inline-block", width: 13, height: 11, background: "#ede9fe", borderRadius: 2, marginRight: 4 }} />他＝他の船（押すとその船の表へ）</span>
        <span><i style={{ display: "inline-block", width: 13, height: 11, background: "#dbeafe", borderRadius: 2, marginRight: 4, boxShadow: "inset 0 -3px 0 #f59e0b" }} />執職（役職と異なる職で従事）</span>
        <span><i style={{ display: "inline-block", width: 13, height: 11, background: "#bbf7d0", borderRadius: 2, marginRight: 4 }} />希望休（自分の行のみ）</span>
      </div>
    </div>
  );
};

const BoardingSheetPage: React.FC<Props> = ({ user: _user }) => {
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [vesselId, setVesselId] = useState("");
  const [ym, setYm] = useState(ymNow());
  const [publishedUntil, setPublishedUntil] = useState<string | null>(null);
  /** 遡れる月数（マスタ設定。既定12ヶ月） */
  const [pastMonths, setPastMonths] = useState(12);
  const [sheet, setSheet] = useState<CrewSheet | null>(null);
  /** 船をまたいだ自分の予定（上部の「あなたの乗船予定」） */
  const [schedule, setSchedule] = useState<ScheduleSeg[]>([]);
  /** カレンダーで選んでいる日（1〜31）。その日にこの船へ誰がいるかを下に出す */
  const [selDay, setSelDay] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  /** 端末に保存した内容を表示しているとき、その保存日時（圏外表示） */
  const [offlineAt, setOfflineAt] = useState<string | null>(null);

  /* ── 表示の切替（crew_boarding_grid の会社だけ）──
     第1ビュー＝紙のExcelと同じ「一覧表」、第2ビュー＝従来の「カレンダー」。
     フラグOFFの会社は従来どおりカレンダーのみ。選んだ方は端末に覚えておく。 */
  const gridEnabled = hasFeature("crew_boarding_grid");
  const [view, setView] = useState<"grid" | "cal">(() => {
    try { return localStorage.getItem("catms_bsheet_view") === "cal" ? "cal" : "grid"; } catch { return "grid"; }
  });
  const switchView = (v: "grid" | "cal") => {
    setView(v);
    try { localStorage.setItem("catms_bsheet_view", v); } catch { /* noop */ }
  };
  const effView: "grid" | "cal" = gridEnabled ? view : "cal";

  /* ── 閲覧できる船と公開範囲を取得 ── */
  useEffect(() => {
    (async () => {
      type VesselsRes = { vessels: Vessel[]; published_until: string | null; past_months?: number };
      const apply = (d: VesselsRes) => {
        const list = d.vessels ?? [];
        setVessels(list);
        setPublishedUntil(d.published_until ?? null);
        if (typeof d.past_months === "number") setPastMonths(d.past_months);
        if (list.length) setVesselId(list[0]!.id);
        // 今月がまだ公開されていない場合は、公開済みの最新月を開く
        const pub = d.published_until ?? null;
        if (pub && pub.slice(0, 7) < ymNow()) setYm(pub.slice(0, 7));
      };
      try {
        const r = await apiFetch<{ data: VesselsRes }>("/boarding-sheet/crew/vessels");
        apply(r.data);
        cacheSave("vessels", r.data);
      } catch (e: any) {
        // 圏外なら前回の内容で開く（沖では通信できないのが普通のため）
        const c = cacheRead<VesselsRes>("vessels");
        if (c) { apply(c.data); setOfflineAt(c.savedAt); }
        else setError(e?.message ?? "読み込みに失敗しました");
      }
      setLoading(false);
    })();
  }, []);

  /* ── 船をまたいだ自分の予定（月を切り替えても取り直さない） ── */
  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch<{ data: { segments: ScheduleSeg[] } }>("/boarding-sheet/crew/my-schedule");
        setSchedule(r.data?.segments ?? []);
        cacheSave("schedule", r.data?.segments ?? []);
      } catch {
        const c = cacheRead<ScheduleSeg[]>("schedule");
        setSchedule(c?.data ?? []);
      }
    })();
  }, []);

  /* ── 選択中の船・月の乗下船表 ── */
  const load = useCallback(async () => {
    if (!vesselId) return;
    setLoading(true); setError("");
    const key = `sheet_${vesselId}_${ym}`;
    try {
      const r = await apiFetch<{ data: CrewSheet }>(`/boarding-sheet/crew?vessel_id=${encodeURIComponent(vesselId)}&month=${ym}`);
      setSheet(r.data);
      setPublishedUntil(r.data?.published_until ?? null);
      setOfflineAt(null);
      cacheSave(key, r.data);
    } catch (e: any) {
      // 圏外・通信断は、前に開いたことのある船・月なら保存した内容を出す
      const c = cacheRead<CrewSheet>(key);
      if (c) {
        setSheet(c.data);
        setPublishedUntil(c.data?.published_until ?? null);
        setOfflineAt(c.savedAt);
      } else {
        setSheet(null);
        setOfflineAt(null);
        setError(e?.message ?? "読み込みに失敗しました");
      }
    }
    setLoading(false);
  }, [vesselId, ym]);
  useEffect(() => { load(); }, [load]);

  const today = todayYmd();
  const [y, m] = ym.split("-").map(Number);

  /** 表示する日（公開範囲まで） */
  const days = useMemo(() => {
    if (!sheet) return [];
    return Array.from({ length: sheet.visible_days }, (_, i) => {
      const n = i + 1;
      const ymd = `${ym}-${String(n).padStart(2, "0")}`;
      return { n, ymd, wd: new Date(ymd + "T00:00:00Z").getUTCDay(), isToday: ymd === today };
    });
  }, [sheet, ym, today]);

  /** 次の月へ進めるか（公開範囲を超える月は開けない） */
  const maxYm = publishedUntil ? publishedUntil.slice(0, 7) : ymNow();
  const canNext = ymAdd(ym, 1) <= maxYm;
  /** 前の月へ戻れるか（マスタで設定した「遡れる月数」まで） */
  const minYm = ymAdd(ymNow(), -pastMonths);
  const canPrev = ymAdd(ym, -1) >= minYm;

  /* 月や船を切り替えたら、選ぶ日は「今日（当月のとき）」に戻す */
  useEffect(() => {
    if (!sheet) { setSelDay(null); return; }
    const t = ym === ymNow() ? Number(today.slice(8, 10)) : 1;
    setSelDay(Math.min(t, sheet.visible_days || 1) || null);
  }, [sheet, ym, today]);

  /** 選んだ日にこの船へ誰がいるか（サーバーから来ている日別セルを読むだけ） */
  const dayPeople = useMemo(() => {
    if (!sheet || !selDay) return null;
    const i = selDay - 1;
    const onBoard: Array<{ row: CrewRow; c: DayCell }> = [];
    const other: Array<{ row: CrewRow; c: DayCell }> = [];
    const off: CrewRow[] = [];
    for (const u of sheet.crew) {
      const c = u.days[i];
      if (!c) continue;
      if (c.s === "on") (c.x ? other : onBoard).push({ row: u, c });
      else if (c.s === "off") off.push(u);
    }
    return { onBoard, other, off };
  }, [sheet, selDay]);

  /** 選んでいる日の YYYY-MM-DD と「8/30（日）」形式の見出し */
  const selDayYmd = selDay ? `${ym}-${String(selDay).padStart(2, "0")}` : "";
  const selDayLabel = selDayYmd
    ? `${m}/${selDay}（${YOUBI[new Date(selDayYmd + "T00:00:00Z").getUTCDay()]}）`
    : "";

  /** 表示中の船を切り替える（見られる船のときだけ。月も合わせる） */
  const jumpTo = (vid?: string, startYmd?: string) => {
    if (!vid || !vessels.some(v => v.id === vid)) return;
    setVesselId(vid);
    const t = (startYmd ?? "").slice(0, 7);
    if (t && t >= minYm && t <= maxYm) setYm(t);
  };

  const me = sheet?.crew.find(c => c.is_me) ?? null;
  // ▲▼は「表示中の船」の乗下船だけ（他の船の分は上の「あなたの乗船予定」で見せる）
  const myBoardDays = (me?.days ?? []).map((c, i) => (c.b && !c.x ? i + 1 : 0)).filter(Boolean);
  const myLeaveDays = (me?.days ?? []).map((c, i) => (c.d && !c.x ? i + 1 : 0)).filter(Boolean);
  const myToday = me && ym === ymNow() ? me.days[Number(today.slice(8, 10)) - 1] : undefined;
  /** 今日が表示中の月の何日目か（0起点。当月以外は -1） */
  const todayIdx = ym === ymNow() ? Number(today.slice(8, 10)) - 1 : -1;

  /** 自分の月カレンダー1マスの色と短いラベル（スマホで一目で分かる表現にする） */
  const myDayStyle = (c?: DayCell): { bg: string; fg: string; border: string; label: string } => {
    if (!c) return { bg: "#fff", fg: "#cbd5e1", border: "#f1f5f9", label: "" };
    if (c.s === "on") {
      // 他の船に乗る日は、その船の乗船日・下船日であっても表示中の船の乗下船と紛らわしいので
      // 必ず紫（他の船）で出し、▲▼と船名を添える
      if (c.x) return { bg: "#ede9fe", fg: "#6d28d9", border: "#ddd6fe", label: `${c.b ? "▲" : c.d ? "▼" : ""}${c.vn ?? "他の船"}` };
      if (c.b) return { bg: "#0057d9", fg: "#fff", border: "#0057d9", label: "乗船" };
      if (c.d) return { bg: "#0057d9", fg: "#fff", border: "#0057d9", label: "下船" };
      return { bg: "#dbeafe", fg: "#0b4e8f", border: "#bfdbfe", label: "乗船中" };
    }
    if (c.w === "approved") return { bg: "#bbf7d0", fg: "#166534", border: "#86efac", label: "希望休" };
    if (c.w === "pending") return { bg: "#fef3c7", fg: "#92400e", border: "#fde68a", label: "申請中" };
    if (c.s === "off") return { bg: "#f8fafc", fg: "#94a3b8", border: "#e2e8f0", label: "休み" };
    return { bg: "#fff", fg: "#cbd5e1", border: "#f1f5f9", label: "" };
  };

  const cellTitle = (c: DayCell, u: CrewRow, ymd: string) => {
    const p = [`${u.user_name} ${md(ymd)}`];
    if (c.s === "on") p.push(c.x ? `${c.vn ?? "他の船"}に乗船` : c.b ? "乗船日" : c.d ? "下船日" : "乗船中");
    else if (c.s === "off") p.push("休（陸上）");
    if (c.w === "approved") p.push("希望休（承認済）");
    else if (c.w === "pending") p.push("希望休（申請中）");
    return p.join(" / ");
  };
  const card: React.CSSProperties = {
    background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)", overflow: "hidden",
  };

  /* ── 表示できない状態の案内 ── */
  const notice = (() => {
    if (loading) return null;
    if (error) {
      return navigator.onLine
        ? { icon: "⚠️", title: "表示できません", body: error }
        : {
            icon: "📡", title: "圏外のため表示できません",
            body: "この船・この月はまだ端末に保存されていません。電波の届くところで一度開くと、次からは圏外でも見られます。",
          };
    }
    if (!vessels.length) return { icon: "🚢", title: "配乗されている船がありません", body: "配乗が登録されると、その船の乗下船表を見られるようになります。" };
    if (sheet && !sheet.published) return { icon: "🔒", title: "まだ公開されていません", body: "陸上の担当者が公開すると、この画面に乗下船表が表示されます。" };
    if (sheet && sheet.visible_days === 0) {
      return {
        icon: "📅", title: `${y}年${m}月はまだ公開されていません`,
        body: publishedUntil ? `現在は ${md(publishedUntil)} までが公開されています。` : "",
      };
    }
    return null;
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, background: "#f8fafc", fontFamily: "'Inter','Hiragino Sans',sans-serif" }}>

      {/* ヘッダー（ノッチ対応の共通ヘッダー。月ナビ・公開範囲・船の切替を下段に置く） */}
      <AppHeader title="乗下船表" subtitle={sheet?.vessel_name ? `🚢 ${sheet.vessel_name}` : ""} padX={14}
        right={offlineAt ? (
          <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 800, color: "#92400e", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 6, padding: "3px 8px" }}>
            オフライン
          </span>
        ) : undefined}>

        {/* 月ナビ（押しやすい大きさで横幅いっぱいに） */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f8fafc", borderRadius: 12, padding: 6, border: "1px solid #e2e8f0", marginBottom: 10 }}>
          <button onClick={() => canPrev && setYm(ymAdd(ym, -1))} disabled={!canPrev}
            title={canPrev ? "" : `過去 ${pastMonths} ヶ月分まで表示できます`}
            style={{ width: 44, height: 40, border: "none", background: canPrev ? "#fff" : "#f1f5f9", borderRadius: 10, cursor: canPrev ? "pointer" : "not-allowed", fontSize: 20, color: canPrev ? "#334155" : "#cbd5e1", boxShadow: canPrev ? "0 1px 2px rgba(0,0,0,0.08)" : "none" }}>‹</button>
          <span style={{ flex: 1, fontSize: 17, fontWeight: 800, color: "#0f172a", textAlign: "center" }}>{y}年{m}月</span>
          <button onClick={() => canNext && setYm(ymAdd(ym, 1))} disabled={!canNext}
            title={canNext ? "" : "これより先はまだ公開されていません"}
            style={{ width: 44, height: 40, border: "none", background: canNext ? "#fff" : "#f1f5f9", borderRadius: 10, cursor: canNext ? "pointer" : "not-allowed", fontSize: 20, color: canNext ? "#334155" : "#cbd5e1", boxShadow: canNext ? "0 1px 2px rgba(0,0,0,0.08)" : "none" }}>›</button>
        </div>

        {/* 表示の切替（一覧表＝紙のExcelと同じ見た目／カレンダー＝従来の表示） */}
        {gridEnabled && (
          <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 10, padding: 3, border: "1px solid #e2e8f0" }}>
            {([["grid", "📋 一覧表"], ["cal", "🗓 カレンダー"]] as const).map(([k, label]) => (
              <button key={k} onClick={() => switchView(k)}
                style={{
                  flex: 1, padding: "8px 0", border: "none", borderRadius: 8, cursor: "pointer",
                  fontSize: 12.5, fontWeight: 800,
                  background: view === k ? "#fff" : "transparent",
                  color: view === k ? "#0f172a" : "#64748b",
                  boxShadow: view === k ? "0 1px 2px rgba(0,0,0,0.10)" : "none",
                }}>
                {label}
              </button>
            ))}
          </div>
        )}

        {/* 公開範囲（陸上が「ここまで」と決めた日） */}
        <div style={{ marginTop: 8, fontSize: 11, color: publishedUntil ? "#0369a1" : "#b45309", background: publishedUntil ? "#f0f9ff" : "#fffbeb", border: `1px solid ${publishedUntil ? "#bae6fd" : "#fde68a"}`, borderRadius: 8, padding: "6px 10px", fontWeight: 700 }}>
          {publishedUntil
            ? `陸上が公開した ${publishedUntil.slice(0, 4)}年${Number(publishedUntil.slice(5, 7))}月分まで見られます`
            : "陸上からの公開待ちです"}
        </div>

        {/* 保存した内容を出しているときは、いつ時点かを必ず添える */}
        {offlineAt && (
          <div style={{ marginTop: 6, fontSize: 11, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "6px 10px", fontWeight: 700 }}>
            圏外のため、{savedAtLabel(offlineAt)} に取得した内容を表示しています
          </div>
        )}

        {/* 船の切替（2隻以上に配乗されている人だけ） */}
        {vessels.length > 1 && (
          <div style={{ display: "flex", gap: 6, marginTop: 8, overflowX: "auto", paddingBottom: 2 }}>
            {vessels.map(v => (
              <button key={v.id} onClick={() => setVesselId(v.id)}
                style={{
                  flexShrink: 0, padding: "7px 14px", borderRadius: 999, cursor: "pointer",
                  border: v.id === vesselId ? "1.5px solid #0057d9" : "1.5px solid #e2e8f0",
                  background: v.id === vesselId ? "#0057d9" : "#fff",
                  color: v.id === vesselId ? "#fff" : "#3d5166",
                  fontSize: 12.5, fontWeight: 700, whiteSpace: "nowrap",
                }}>
                🚢 {v.vessel_name}
              </button>
            ))}
          </div>
        )}
      </AppHeader>

      {/* 本文 */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "12px 12px 24px" }}>
        {loading && <div style={{ textAlign: "center", padding: 50, color: "#94a3b8", fontSize: 13 }}>読み込み中...</div>}

        {/* 船をまたいだ自分の予定。1隻1ヶ月の表だけでは乗り継ぎが追えないため先頭に置く（カレンダービューのみ） */}
        {!loading && effView === "cal" && schedule.length > 0 && (
          <div style={{ ...card, marginBottom: 10 }}>
            <div style={{ padding: "12px 14px 6px" }}>
              <div style={{ fontSize: 15, fontWeight: 900, color: "#0f172a" }}>あなたの乗船予定</div>
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 3, lineHeight: 1.6 }}>
                乗る船と陸上（休み）を日付順に並べています。船の行を押すと、その船の乗下船表を開きます。
              </div>
            </div>
            <div style={{ padding: "2px 12px 12px" }}>
              {schedule.map((sg, i) => {
                const end = sg.end ?? "";
                const isNow = sg.start <= today && (sg.open === 1 || today <= end);
                const isPast = !!end && end < today && sg.open !== 1;
                const days = end ? Math.round((Date.parse(end + "T00:00:00Z") - Date.parse(sg.start + "T00:00:00Z")) / 86400000) + 1 : 0;
                const shore = sg.kind === "shore";
                const tone = shore
                  ? { bar: "#cbd5e1", bg: "#f8fafc", fg: "#475569" }
                  : isNow ? { bar: "#0057d9", bg: "#eff6ff", fg: "#0b4e8f" }
                  : isPast ? { bar: "#cbd5e1", bg: "#fff", fg: "#64748b" }
                  : { bar: "#93c5fd", bg: "#f8fbff", fg: "#1d4ed8" };
                const canJump = !shore && !!sg.vessel_id && vessels.some(v => v.id === sg.vessel_id);
                return (
                  <div key={`${sg.kind}-${sg.start}-${i}`}
                    onClick={() => canJump && jumpTo(sg.vessel_id, sg.start)}
                    style={{
                      display: "flex", gap: 10, alignItems: "stretch", marginTop: i === 0 ? 4 : 6,
                      background: tone.bg, border: `1px solid ${isNow ? "#bfdbfe" : "#eef2f6"}`,
                      borderRadius: 12, padding: "9px 11px", cursor: canJump ? "pointer" : "default",
                    }}>
                    <div style={{ width: 4, borderRadius: 999, background: tone.bar, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 14.5, fontWeight: 900, color: shore ? "#475569" : "#0f172a" }}>
                          {shore ? "🏠 陸上（休み）" : `🚢 ${sg.vessel_name ?? "（船名なし）"}`}
                        </span>
                        {!shore && sg.position && (
                          <span style={{ fontSize: 11, fontWeight: 800, color: "#475569", background: "#f1f5f9", borderRadius: 999, padding: "2px 8px" }}>{sg.position}</span>
                        )}
                        {isNow && (
                          <span style={{ fontSize: 11, fontWeight: 900, color: shore ? "#475569" : "#0057d9", background: shore ? "#e2e8f0" : "#dbeafe", borderRadius: 999, padding: "2px 8px" }}>
                            {shore ? "いま陸上" : "いま乗船中"}
                          </span>
                        )}
                        {!isNow && !isPast && (
                          <span style={{ fontSize: 11, fontWeight: 900, color: "#1d4ed8", background: "#e0ecff", borderRadius: 999, padding: "2px 8px" }}>予定</span>
                        )}
                        {isPast && (
                          <span style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8", background: "#f1f5f9", borderRadius: 999, padding: "2px 8px" }}>
                            {shore ? "休み済み" : "乗船済み"}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: tone.fg, marginTop: 3 }}>
                        {md(sg.start)} 〜 {sg.open === 1 ? "この先も続く予定" : md(end)}
                        {days > 0 && <span style={{ color: "#94a3b8", fontWeight: 600 }}>（{days}日間）</span>}
                      </div>
                    </div>
                    {canJump && <span style={{ alignSelf: "center", fontSize: 18, color: "#94a3b8", flexShrink: 0 }}>›</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!loading && notice && (
          <div style={{ ...card, padding: "28px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 34, marginBottom: 10 }}>{notice.icon}</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>{notice.title}</div>
            {notice.body && <div style={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.7 }}>{notice.body}</div>}
          </div>
        )}

        {!loading && !notice && sheet && (
          <>
            {/* 一覧表ビュー（紙のExcel乗下船表と同じ見た目） */}
            {effView === "grid" && (
              <SheetGrid sheet={sheet} days={days} vessels={vessels} jumpTo={jumpTo} />
            )}

            {effView === "cal" && (
            <>
            {/* 自分の予定（日数は出さない・日付だけ） */}
            {me && (
              <div style={{ ...card, padding: "12px 14px", marginBottom: 10, borderLeft: "4px solid #0057d9" }}>
                <div style={{ fontSize: 15, fontWeight: 900, color: "#0f172a", marginBottom: 8 }}>
                  あなた（{me.positions.length ? me.positions.join("／") : "役職未設定"}）
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, fontSize: 13.5, fontWeight: 800 }}>
                  {myToday && (
                    <span style={{ padding: "4px 10px", borderRadius: 999, background: myToday.s === "on" ? "#eff6ff" : "#f1f5f9", color: myToday.s === "on" ? "#0057d9" : "#64748b" }}>
                      今日: {myToday.s === "on" ? (myToday.x ? `${myToday.vn ?? "他の船"}に乗船` : "乗船中") : myToday.s === "off" ? "休（陸上）" : "—"}
                    </span>
                  )}
                  {myBoardDays.map(d => (
                    <span key={`b${d}`} style={{ padding: "4px 10px", borderRadius: 999, background: "#f0fdf4", color: "#166534" }}>▲ {m}/{d} 乗船</span>
                  ))}
                  {myLeaveDays.map(d => (
                    <span key={`d${d}`} style={{ padding: "4px 10px", borderRadius: 999, background: "#fef2f2", color: "#b42318" }}>▼ {m}/{d} 下船</span>
                  ))}
                  {!myToday && !myBoardDays.length && !myLeaveDays.length && (
                    <span style={{ color: "#94a3b8", fontWeight: 600 }}>この月の乗下船の予定はありません</span>
                  )}
                </div>
              </div>
            )}

            {/* 自分の月カレンダー（スマホは横長の表より7列カレンダーの方が読みやすい） */}
            <div style={{ ...card, padding: "10px 10px 12px", marginBottom: 10 }}>
              <div style={{ fontSize: 15, fontWeight: 900, color: "#0f172a", padding: "2px 4px 10px" }}>
                あなたの{m}月
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 3, marginBottom: 4 }}>
                {YOUBI.map((w, i) => (
                  <div key={w} style={{ textAlign: "center", fontSize: 12, fontWeight: 800, color: i === 0 ? "#dc2626" : i === 6 ? "#2563eb" : "#94a3b8" }}>{w}</div>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 3 }}>
                {Array.from({ length: days[0]?.wd ?? 0 }).map((_, i) => <div key={`pad${i}`} />)}
                {days.map((d, i) => {
                  const c = me?.days[i];
                  const st = myDayStyle(c);
                  const isSel = selDay === d.n;
                  return (
                    <div key={d.ymd} title={c ? cellTitle(c, me!, d.ymd) : ""}
                      onClick={() => setSelDay(d.n)}
                      style={{
                        minHeight: 60, borderRadius: 12, padding: "6px 2px",
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1,
                        background: st.bg, cursor: "pointer",
                        border: isSel ? "2.5px solid #0f172a" : d.isToday ? "2px solid #f59e0b" : `1px solid ${st.border}`,
                        boxShadow: isSel ? "0 0 0 3px rgba(15,23,42,0.10)" : "none",
                      }}>
                      <span style={{ fontSize: 16, fontWeight: 900, color: st.fg }}>{d.n}</span>
                      <span style={{
                        fontSize: st.label.length > 5 ? 8 : st.label.length > 3 ? 9.5 : 11,
                        fontWeight: 800, color: st.fg, lineHeight: 1.1, textAlign: "center",
                        wordBreak: "break-all", maxWidth: "100%", overflow: "hidden",
                      }}>{st.label}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: 12, fontWeight: 700, color: "#475569", marginTop: 12, padding: "0 2px" }}>
                <span><i style={{ display: "inline-block", width: 12, height: 10, background: "#dbeafe", borderRadius: 2, marginRight: 4 }} />乗船</span>
                <span><i style={{ display: "inline-block", width: 12, height: 10, background: "#0057d9", borderRadius: 2, marginRight: 4 }} />乗船日・下船日</span>
                <span><i style={{ display: "inline-block", width: 12, height: 10, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 2, marginRight: 4 }} />休み</span>
                <span><i style={{ display: "inline-block", width: 12, height: 10, background: "#ede9fe", borderRadius: 2, marginRight: 4 }} />他の船（船名を表示・押すとその船へ）</span>
                <span><i style={{ display: "inline-block", width: 12, height: 10, background: "#bbf7d0", borderRadius: 2, marginRight: 4 }} />希望休</span>
              </div>
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 8, padding: "0 2px" }}>
                日付を押すと、その日にこの船へ誰が乗っているかを下に出します。
              </div>
            </div>

            {/* 選んだ日にこの船へ誰がいるか */}
            {dayPeople && selDay && (
              <div style={{ ...card, marginBottom: 10, borderLeft: "4px solid #0f172a" }}>
                <div style={{ padding: "12px 14px 6px" }}>
                  <div style={{ fontSize: 15, fontWeight: 900, color: "#0f172a" }}>
                    {selDayLabel} の乗船者
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    {sheet.vessel_name} に {dayPeople.onBoard.length} 名
                  </div>
                </div>

                {/* この船に乗っている人 */}
                <div style={{ padding: "0 12px 4px" }}>
                  {dayPeople.onBoard.length === 0 && (
                    <div style={{ fontSize: 12.5, color: "#94a3b8", padding: "6px 2px 10px" }}>この日にこの船へ乗っている人はいません。</div>
                  )}
                  {dayPeople.onBoard.map(({ row, c }) => (
                    <div key={row.user_id} style={{
                      display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", marginTop: 6,
                      background: row.is_me ? "#eff6ff" : "#f8fafc",
                      border: `1px solid ${row.is_me ? "#bfdbfe" : "#eef2f6"}`, borderRadius: 10,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 14.5, fontWeight: 900, color: "#0f172a" }}>{row.user_name}</span>
                        {row.is_me && <span style={{ fontSize: 11, fontWeight: 800, color: "#0057d9", marginLeft: 6 }}>（あなた）</span>}
                        {row.positions[0] && <span style={{ fontSize: 12.5, fontWeight: 700, color: "#64748b", marginLeft: 6 }}>{row.positions[0]}</span>}
                      </div>
                      {c.b === 1 && <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 900, color: "#166534", background: "#f0fdf4", borderRadius: 999, padding: "4px 10px" }}>▲ この日 乗船</span>}
                      {c.d === 1 && <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 900, color: "#b42318", background: "#fef2f2", borderRadius: 999, padding: "4px 10px" }}>▼ この日 下船</span>}
                    </div>
                  ))}
                </div>

                {/* 他の船に乗っている人（船名を出し、押すとその船の表へ） */}
                {dayPeople.other.length > 0 && (
                  <div style={{ padding: "8px 12px 4px" }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#6d28d9", padding: "0 2px 2px" }}>他の船に乗っている人</div>
                    {dayPeople.other.map(({ row, c }) => {
                      const canJump = !!c.vi && vessels.some(v => v.id === c.vi);
                      return (
                        <div key={row.user_id}
                          onClick={() => canJump && jumpTo(c.vi, selDayYmd)}
                          style={{
                            display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", marginTop: 6,
                            background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 10,
                            cursor: canJump ? "pointer" : "default",
                          }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 14.5, fontWeight: 900, color: "#0f172a" }}>{row.user_name}</span>
                            {row.is_me && <span style={{ fontSize: 11, fontWeight: 800, color: "#0057d9", marginLeft: 6 }}>（あなた）</span>}
                            {row.positions[0] && <span style={{ fontSize: 12.5, fontWeight: 700, color: "#64748b", marginLeft: 6 }}>{row.positions[0]}</span>}
                          </div>
                          <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 900, color: "#6d28d9", background: "#ede9fe", borderRadius: 999, padding: "4px 10px" }}>
                            {c.vn ?? "他の船"}
                          </span>
                          {canJump && <span style={{ flexShrink: 0, fontSize: 16, color: "#94a3b8" }}>›</span>}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* 陸上（休み）の人 */}
                {dayPeople.off.length > 0 && (
                  <div style={{ padding: "8px 14px 14px" }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#64748b", marginBottom: 5 }}>陸上（休み）</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {dayPeople.off.map(u => (
                        <span key={u.user_id} style={{ fontSize: 12.5, fontWeight: 700, color: "#475569", background: "#f1f5f9", borderRadius: 999, padding: "4px 10px" }}>
                          {u.user_name}{u.is_me ? "（あなた）" : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 同じ船の乗組員（横長の表をやめ、1人1行のリストにする） */}
            <div style={{ ...card, marginBottom: 10, overflow: "hidden" }}>
              <div style={{ padding: "12px 12px 8px" }}>
                <div style={{ fontSize: 15, fontWeight: 900, color: "#0f172a" }}>この船の乗組員（{m}月）</div>
                <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2 }}>
                  右のバッジは今日（{md(today)}）の状態です。日ごとに見るには上のカレンダーの日付を押してください。
                </div>
              </div>
              {sheet.crew.filter(u => !u.is_me).map(u => {
                const t = todayIdx >= 0 ? u.days[todayIdx] : undefined;
                const bs = u.days.map((c, i) => (c.b && !c.x ? i + 1 : 0)).filter(Boolean);
                const ds = u.days.map((c, i) => (c.d && !c.x ? i + 1 : 0)).filter(Boolean);
                const state = !t ? null
                  : t.s === "on" ? (t.x ? { label: t.vn ?? "他の船", color: "#6d28d9", bg: "#f5f3ff" } : { label: "乗船中", color: "#0057d9", bg: "#eff6ff" })
                  : t.s === "off" ? { label: "休み", color: "#64748b", bg: "#f1f5f9" }
                  : null;
                return (
                  <div key={u.user_id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderTop: "1px solid #f1f5f9" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15.5, fontWeight: 900, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {u.user_name}
                        {u.positions[0] && <span style={{ fontSize: 13, fontWeight: 700, color: "#64748b", marginLeft: 6 }}>{u.positions[0]}</span>}
                      </div>
                      {(bs.length > 0 || ds.length > 0) && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 4 }}>
                          {bs.map(d => <span key={`b${d}`} style={{ fontSize: 12.5, fontWeight: 800, color: "#166534", background: "#f0fdf4", borderRadius: 999, padding: "4px 10px" }}>▲ {m}/{d} 乗船</span>)}
                          {ds.map(d => <span key={`d${d}`} style={{ fontSize: 12.5, fontWeight: 800, color: "#b42318", background: "#fef2f2", borderRadius: 999, padding: "4px 10px" }}>▼ {m}/{d} 下船</span>)}
                        </div>
                      )}
                    </div>
                    {state && (
                      <span style={{ flexShrink: 0, fontSize: 13.5, fontWeight: 900, color: state.color, background: state.bg, borderRadius: 999, padding: "6px 14px" }}>
                        {state.label}
                      </span>
                    )}
                  </div>
                );
              })}
              {sheet.crew.filter(u => !u.is_me).length === 0 && (
                <div style={{ padding: "16px 12px 20px", fontSize: 12.5, color: "#94a3b8", textAlign: "center" }}>
                  ほかに配乗されている乗組員はいません
                </div>
              )}
            </div>
            </>
            )}

            {/* 乗下船イベント（どちらのビューでも出す） */}
            <div style={{ ...card, padding: "12px 14px" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>⚓ 乗下船の予定（{m}月）</div>
              {sheet.events.length === 0 && (
                <div style={{ fontSize: 12, color: "#94a3b8" }}>公開されている範囲に予定はありません。</div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {sheet.events.map(e => {
                  const t = EVENT_TYPE[e.event_type] ?? EVENT_TYPE["other"]!;
                  return (
                    <div key={e.id} style={{ border: "1px solid #eef2f6", borderRadius: 10, padding: "9px 11px", background: "#fcfdfe" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
                          {md(e.event_date)}{e.end_date ? `〜${md(e.end_date)}` : ""}
                        </span>
                        <span style={{ fontSize: 10.5, fontWeight: 700, color: t.color, background: t.bg, borderRadius: 6, padding: "2px 8px" }}>{t.label}</span>
                        {e.port_name && <span style={{ fontSize: 12, fontWeight: 700, color: "#3d5166" }}>⚓ {e.port_name}</span>}
                        {e.berth_note && <span style={{ fontSize: 11, color: "#94a3b8" }}>{e.berth_note}</span>}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {e.items.filter(i => i.action === "board").map((i, k) => (
                          <span key={`b${k}`} style={{ fontSize: 11, fontWeight: 700, color: "#166534", background: "#f0fdf4", borderRadius: 6, padding: "3px 8px" }}>
                            ▲ {i.user_name}{i.position ? `（${i.position}）` : ""}
                          </span>
                        ))}
                        {e.items.filter(i => i.action === "disembark").map((i, k) => (
                          <span key={`d${k}`} style={{ fontSize: 11, fontWeight: 700, color: "#b42318", background: "#fef2f2", borderRadius: 6, padding: "3px 8px" }}>
                            ▼ {i.user_name}{i.position ? `（${i.position}）` : ""}
                          </span>
                        ))}
                      </div>
                      {e.note && <div style={{ marginTop: 4, fontSize: 11, color: "#64748b" }}>※{e.note}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default BoardingSheetPage;
